// Webpack Imports
import * as bootstrap from 'bootstrap';
import PhotoSwipeLightbox from "photoswipe/lightbox";
import PhotoSwipe from "photoswipe";


const COMMON_GALLERY_PROPERTIES = {
    closeTitle: 'Fermer',
    zoomTitle: 'Zoomer',
    arrowPrevTitle: 'Précédente',
    arrowNextTitle: 'Suivante',
    errorMsg: 'Cette image n\'a pas pu être chargée',
    pswpModule: PhotoSwipe,
    bgOpacity: 1,
    padding: {top: 16, right: 16, left: 16, bottom: 16}
}

class SbmGallery {

    static THUMBNAIL_X_PADDING = 40;

    static THUMBNAIL_Y_PADDING = 16;

    static THUMBNAIL_HEIGHT = 150;


    /**
     * @type HTMLElement
     */
    element

    /**
     *
     * The Lightbox instance.
     *
     * @type {PhotoSwipeLightbox | null}
     */
    lightbox = null;

    /**
     *
     * @type {SlideData[]}
     */
    sources = [];

    /**
     *
     * @type {Array<{src: string}>}
     */
    thumbnails = [];


    /**
     * Caption display after the
     * @type {string | null}
     */
    caption = null;

    /**
     *
     * @param {HTMLElement} element - the gallery element
     */
    constructor(element) {
        this.element = element;
        this.parseElement();
        this.initGallery();
    }


    hasGallery() {
        return this.lightbox instanceof PhotoSwipeLightbox;
    }

    parseElement() {

        const caption = this.element.dataset.sbGalleryCaption;

        if (caption) {
            this.caption = caption;
        }

        try {
            const data = this.element.dataset.sbGallery;

            if (typeof data !== "string" || !data) {
                return;
            }

            const raw = JSON.parse(data);


            if (!Array.isArray(raw)) {
                return;
            }


            /** @type {HTMLTemplateElement} **/
            const template = this.element.querySelector('.gallery-template');

            if (!template) {
                return;
            }

            raw.forEach((item, index) => {

                if (typeof item.full === 'string' && item.full) {
                    const html = document.importNode(template.content, true);
                    const imageWrapper = html.querySelector('.gallery-image');

                    if (!imageWrapper) {
                        return;
                    }

                    /** @type {HTMLImageElement} **/
                    const image = document.createElement('img');
                    image.src = item.full;

                    if (item.width) {
                        image.width = item.width;
                    }

                    if (item.height) {
                        image.height = item.height;
                    }

                    imageWrapper.appendChild(image);

                    /** @type {HTMLElement} **/
                    const div = document.createElement('div');
                    div.classList.add('sb-gallery-item')
                    div.append(html);


                    this.sources.push({html: div.outerHTML});

                    if (typeof item.thumbnail === 'string' && item.thumbnail) {
                        this.thumbnails.push({src: item.thumbnail, index});
                    }
                }

            });

        } catch (e) {
            console.error(e);
        }
    }


    initGallery() {

        if (this.hasGallery() || this.sources.length === 0) {
            return;
        }

        /**
         * @type {PreparedPhotoSwipeOptions}
         */
        const options = {
            ...COMMON_GALLERY_PROPERTIES,
            dataSource: this.sources,
            pswpModule: PhotoSwipe,
            padding: {
                top: 16,
                left: 0,
                right: 0,
                bottom: (SbmGallery.THUMBNAIL_Y_PADDING * 2 + SbmGallery.THUMBNAIL_HEIGHT + 16)
            }

        }

        this.lightbox = new PhotoSwipeLightbox(options);

        this.registerUiElements();
        this.registerEvents();
        this.lightbox.init();
    }


    registerUiElements() {
        if (!this.hasGallery()) {
            return;
        }

        const registerCaption = () => {
            if (!this.caption) {
                return;
            }

            this.lightbox.pswp.ui.registerElement({
                name: 'custom-caption',
                order: 9,
                appendTo: 'wrapper',
                html: this.caption,
            });
        }

        const registerThumbnailsNav = () => {
            this.lightbox.pswp.ui.registerElement({
                name: 'thumbnail-gallery',
                className: 'thumbnail-nav-container',
                appendTo: 'wrapper',
                onInit: (wrapper, pswp) => {

                    /** @type {HTMLElement} **/
                    const nav = document.createElement('nav');
                    nav.classList.add('thumbnail-nav-wrapper');
                    wrapper.appendChild(nav);

                    /** @type {HTMLUListElement} **/
                    const list = document.createElement('ul');
                    list.classList.add('thumbnail-nav');
                    nav.appendChild(list);

                    const directions = ['prev', 'next'];

                    directions.forEach(direction => {

                        /** @type {HTMLButtonElement} **/
                        const button = document.createElement('button');
                        button.type = 'button';
                        button.dataset.bsThumbnailDirection = direction;
                        button.ariaLabel = direction === 'prev' ? `Miniature précédente` : `Miniature suivante`;
                        button.innerHTML = '<svg aria-hidden="true" class="icon" width="19" height="32"><use xlink:href="#bs-gallery-arrow"></use></svg>'

                        button.addEventListener('click', () => this.scrollThumbnailList(list, direction));

                        nav.appendChild(button);

                    });


                    /** @type {HTMLLIElement[]} **/
                    const items = [];

                    /** @type {HTMLLIElement} **/
                    let item;
                    let prevIndex = -1;

                    for (let i = 0; i < pswp.getNumItems(); i++) {

                        const thumbnail = this.thumbnails[i];

                        if (!thumbnail) {
                            continue;
                        }

                        item = document.createElement('li');

                        /** @type {HTMLImageElement} **/
                        const image = document.createElement('img');
                        image.src = thumbnail.src;
                        image.ariaLabel = `Aller à l'image ${i + 1}`;

                        item.append(image);

                        item.onclick = (e) => {
                            pswp.goTo(items.indexOf(e.target));
                        };

                        list.appendChild(item);
                        items.push(item);
                    }

                    pswp.on('change', (a,) => {
                        if (prevIndex >= 0) {
                            items[prevIndex].classList.remove('thumbnail-active');
                        }
                        items[pswp.currIndex].classList.add('thumbnail-active');
                        prevIndex = pswp.currIndex;
                    });
                }
            });
        }

        // Add thumbnail navigation
        this.lightbox.on('uiRegister', () => {
            //registerCaption();
            registerThumbnailsNav();
        });
    }


    registerEvents() {
        this.lightbox.on('openingAnimationStart', () => {

            if (!this.hasGallery()) {
                return;
            }


            const options = this.lightbox.pswp.options;

            if (options.padding) {

                Object.keys(options.padding).forEach(position => {
                    document.body.style.setProperty(`--wp--custom--bs-gallery--padding-${position}`, `${options.padding[position]}px`);
                });
            }
        });

        this.lightbox.on('closingAnimationEnd', () => {
            ['top', 'right', 'bottom', 'left'].forEach(position => {
                document.body.style.removeProperty(`--wp--custom--bs-gallery--padding-${position}`);
            });
        });
    }

    /**
     *
     * @param {HTMLUListElement} list
     * @param {'prev' | 'next'} direction
     */
    scrollThumbnailList(list, direction) {
        if (!list) {
            return;
        }

        const item = list.querySelector('li');

        if (!item) {
            return;
        }

        const count = list.querySelectorAll('li').length;

        const width = list.getBoundingClientRect().width;
        const itemWidth = item.getBoundingClientRect().width;
        const visibleMax = Math.round(width / (itemWidth + 40));

        let scrolled = list.dataset.bsScrolledItem ? parseInt(list.dataset.bsScrolledItem) : 0;

        const scroll = direction === "prev" ? scrolled - 1 : scrolled + 1;


        if (scroll === -1 || scroll > count) {
            return;
        }

        const translate = scroll * (itemWidth + SbmGallery.THUMBNAIL_X_PADDING);

        list.style.setProperty('transform', `translateX(-${translate}px)`);
        list.dataset.bsScrolledItem = scroll.toString();

    }
}

class Sbm {

    /**
     * @type { Sbm }
     */
    static instance;

    ANCHOR_SCROLL_OFFSET = 16;
    /**
     *  Page resize callback
     * @type {Array<Function>}
     */
    resizeObservers = [];

    header = {element: undefined, height: 0};

    constructor() {

        if (!Sbm.instance) {
            this.resize = this.resize.bind(this);
            this.trackHeaderHeight();
            this.watchMenuAnchors();
            this.createReferenceGalleries();
            this.createSingleReferenceGallery();
            this.createBannerSlider();
            this.createTraitementGallery();

            window.addEventListener('resize', this.resize);
        }

        Sbm.instance = this;
    }

    /**
     *  Call each callable set in resizeObservers array
     */
    resize() {
        this.resizeObservers.forEach(callback => callback());
    }

    trackHeaderHeight() {
        const header = document.getElementById('branding-header');

        if (!header) {
            return;
        }

        this.header.element = header;
        this.setHeaderHeight(header.getBoundingClientRect().height);

        // Change header value on window resize
        this.resizeObservers.push(() => {
            const height = header.getBoundingClientRect().height;
            if (this.header.height !== height) {
                this.setHeaderHeight(height);
            }
        });

    }

    /**
     * Set header height and update css variable on body
     * @param {number} height
     */
    setHeaderHeight(height) {
        this.header.height = height;
        this.headerHeight = document.body.style.setProperty('--wp--custom--header--height', `${height}px`);
    }

    watchMenuAnchors() {

        /** @type {NodeListOf<HTMLAnchorElement>} **/
        const navBarLinks = document.querySelectorAll('.navbar a[href*="#"]');

        /** @type {NodeListOf<HTMLAnchorElement>} **/
        const otherAnchorlinks = document.querySelectorAll(':not(.navbar) a[href*="#"]');

        const location = new URL(window.location);


        const isCurrentPageAnchor = (url) => {
            return url.origin === location.origin && this.trimSlashes(url.pathname) === this.trimSlashes(location.pathname);
        }

        const setNavigationListener = (link) => {
            const url = new URL(link.href);

            if (isCurrentPageAnchor(url)) {
                link.classList.add('current-menu-item-anchor');
                link.addEventListener("click", event => this.navigateTo(event, url.hash));
            }
        }

        const setNavbarCollapseListener = (link) => {

            /** @type {NodeListOf<HTMLElement> | null} **/
            let launchers = null;

            /** @type {HTMLElement | null} **/
            const parent = link.closest('.navbar-collapse');


            if (parent) {
                if (parent.id) {
                    const id = parent.id;

                    /** @type {HTMLElement | null} **/
                    const root = parent.closest('.navbar');

                    if (root) {
                        launchers = root.querySelectorAll(`[data-bs-toggle="collapse"][data-bs-target="#${id}"]`);
                    }
                }

                link.addEventListener('click', () => this.collapseNavbar(parent, launchers));
            }

        }

        otherAnchorlinks.forEach(link => {
            try {
                setNavigationListener(link);
            } catch (e) {
                console.error(e);
            }
        });

        navBarLinks.forEach(link => {
            try {
                setNavigationListener(link);
                setNavbarCollapseListener(link);
            } catch (e) {
                console.error(e);
            }
        });

    }

    createReferenceGalleries() {

        /** @type {NodeListOf<HTMLElement>} **/
        const references = document.querySelectorAll('.reference[data-sb-gallery]');

        if (references.length === 0) {
            return;
        }

        const galleries = Array.from(references).map(reference => new SbmGallery(reference));

        for (const gallery of galleries) {

            if (gallery.hasGallery()) {
                gallery.element.addEventListener('click', () => {
                    gallery.lightbox.loadAndOpen(0);
                });
            }

        }


    }

    createSingleReferenceGallery() {

        /** @type {HTMLElement | null} **/
        const gallery = document.querySelector('.single-reference #post-content .reference-grid');

        if (!gallery) {
            return;
        }

        const lightbox = new PhotoSwipeLightbox({
            ...COMMON_GALLERY_PROPERTIES,
            gallery: gallery,
            children: 'a',
        });
        lightbox.init();
    }

    createBannerSlider() {
        /** @type {HTMLElement | null} **/
        const slider = document.querySelector('.region-banner .slider');


        if (!slider) {
            return;
        }


        const swiper = new Swiper(slider, {
            direction: 'horizontal',
            autoplay: {
                delay: 2000,
            },
            speed: 1000,
            loop: true,
            navigation: true,
        });


        /** @type { HTMLElement | null } previousButton  **/
        const previousButton = slider.querySelector('.swiper-button-prev');

        /** @type { HTMLElement | null } previousButton  **/
        const nextButton = slider.querySelector('.swiper-button-next');

        if (previousButton) {
            previousButton.setAttribute('aria-controls', swiper.wrapperEl.getAttribute('id'));
            previousButton.addEventListener('click', () => swiper.slidePrev());
        }

        if (nextButton) {
            nextButton.setAttribute('aria-controls', swiper.wrapperEl.getAttribute('id'));
            nextButton.addEventListener('click', () => swiper.slideNext());
        }


    }

    createTraitementGallery() {
        document.querySelectorAll('.section-traitement .gallery-container .swiper').forEach(container => {

            const swiper = new Swiper(container, {
                direction: 'horizontal',
                autoplay: {
                    delay: 2000,
                },
                speed: 1000,
                loop: true,
                slidesPerView: 1,
                spaceBetween: 30,
                pagination: {
                    el: ".swiper-pagination",
                    clickable: true,
                },
                breakpoints: {
                    640: {
                        slidesPerView: 2,
                    },
                    960: {
                        slidesPerView: 3,
                    }
                }
            });

            const lightbox = new PhotoSwipeLightbox({
                // may select multiple "galleries"
                gallery: container,

                // Elements within gallery (slides)
                children: 'a',

                // setup PhotoSwipe Core dynamic import
                pswpModule: PhotoSwipe
            });
            lightbox.init();
        });
    }

    /**
     *
     * @param { MouseEvent } event
     * @param { string } anchor
     */
    navigateTo(event, anchor) {

        if (anchor.startsWith('#')) {
            anchor = anchor.substring(1);
        }

        /** @type {HTMLElement | null} **/
        const target = document.getElementById(anchor);

        if (!target) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        const position = target.getBoundingClientRect();

        let y = target.offsetTop + this.calculateScrollOffset();
        window.scroll(0, y);
    }

    calculateScrollOffset() {
        let offset = this.ANCHOR_SCROLL_OFFSET;

        if (this.header.height) {
            offset = offset - this.header.height;
        }

        /** @type {HTMLElement} **/
        const nav = document.querySelector('.section-metallerie-nav');

        if (nav) {
            offset = offset - nav.getBoundingClientRect().height
        }

        return offset;
    }

    /**
     *
     * @param {string} value
     * @return {string}
     */
    trimSlashes(value) {

        if (typeof value !== 'string') {
            return value;
        }

        while (value.charAt(0) === '/') {
            value = value.substring(1);
        }

        // Supprime les slashes à la fin de la chaîne
        while (value.charAt(value.length - 1) === '/') {
            value = value.substring(0, value.length - 1);
        }

        return value;
    }

    /**
     *
     * @param { HTMLElement } navbar
     * @param { NodeListOf<HTMLElement> |  null } launchers
     */
    collapseNavbar(navbar, launchers) {
        if (navbar && navbar.classList.contains('show')) {
            navbar.classList.remove('show');
        }

        if (launchers && launchers.length > 0) {
            launchers.forEach(launcher => {
                if (launcher.ariaExpanded === 'true') {
                    launcher.ariaExpanded = 'false';
                }
            });
        }

    }
}


(function () {
    'use strict';

    // Focus input if Searchform is empty
    [].forEach.call(document.querySelectorAll('.search-form'), (el) => {
        el.addEventListener('submit', function (e) {
            var search = el.querySelector('input');
            if (search.value.length < 1) {
                e.preventDefault();
                search.focus();
            }
        });
    });


    // Initialize Popovers: https://getbootstrap.com/docs/5.0/components/popovers
    var popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'));
    var popoverList = popoverTriggerList.map(function (popoverTriggerEl) {
        return new bootstrap.Popover(popoverTriggerEl, {
            trigger: 'focus',
        });
    });

    new Sbm();
})();

